/* Hook encargado de crear y modificar el Vehiculo

Autor: Benjamín León
Fecha: 2024-10-01
*/
import { useState } from 'react'

import { VehicleEx } from '../components/VehicleEdit.tsx'
import { customPost } from '../utils/customFetch.ts'
import { Expense } from '../interfaces/VehicleBase.ts'
import { normalizeDate } from '../utils/utils.ts'

import {
  makeUrlVehicleArchive,
  makeUrlVehicleEdit,
  makeUrlVehicleExpensesPost,
  makeUrlVehicleRestore,
  makeUrlVehiclePublishLocal,
} from './connectionData.tsx'
import { useAuth } from './useAuth.tsx'

const useVehiclePoster = () => {
  const [errorPostVehicle, setErrorPostVehicle] = useState<Error | null>(null)
  const [loadingPost, setLoadingPost] = useState<boolean>(false)
  const [vehicleExpensesPostError, setVehicleExpensesPostError] = useState<Error | null>(null)
  const [vehicleArchiveError, setVehicleArchiveError] = useState<Error | null>(null)
  const [vehicleRestoreError, setVehicleRestoreError] = useState<Error | null>(null)
  const [vehiclePublishLocalError, setVehiclePublishLocalError] = useState<Error | null>(null)
  const { userData } = useAuth()
  const postVehicle = async (vehicle: VehicleEx, mode: boolean) => {
    setLoadingPost(true)

    try {
      const id = await updateDatabase(vehicle, mode)
      setErrorPostVehicle(null)

      return id
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorPostVehicle(err)
      } else {
        setErrorPostVehicle(new Error('An unknown error occurred'))
      }

      return null
    } finally {
      setLoadingPost(false)
    }
  }
  const updateDatabase = async (vehicle: VehicleEx, mode: boolean): Promise<string> => {
    const apiUrl = makeUrlVehicleEdit(mode)
    const res = await customPost<{ _id: string }>(apiUrl, vehicle)

    return mode ? 'OK' : res._id
  }
  // Actualiza tanto los gastos (expenses) como los ingresos (incomes)
  const vehicleExpensesPost = async (id: string, expensesFormat: Expense[], isIncome: boolean) => {
    setLoadingPost(true)

    // Vamos a transformar antes de enviar
    const transformToExpense = (expenses: Expense[]): Expense[] => {
      return expenses.map((expense) => ({
        _id: expense._id,
        cost: expense.cost,
        description: expense.description,
        date: normalizeDate(expense.date),
        excluded: expense.excluded,
      }))
    }

    const expenses: Expense[] = transformToExpense(expensesFormat ?? [])

    try {
      const apiUrl = makeUrlVehicleExpensesPost()
      const toPost = { id: id, expenses: expenses, insw: isIncome }
      await customPost<void>(apiUrl, toPost)
      setVehicleExpensesPostError(null)

      return 'OK'
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setVehicleExpensesPostError(err)
      } else {
        setVehicleExpensesPostError(new Error('An unknown error occurred'))
      }

      return null
    } finally {
      setLoadingPost(false)
    }
  }
  const vehicleArchive = async (_id: string, cause: string) => {
    setLoadingPost(true)

    try {
      const apiUrl = makeUrlVehicleArchive()
      const by = userData?.profile?._id ?? ''
      const toPost = { _id, cause, by }
      await customPost<void>(apiUrl, toPost)
      setVehicleArchiveError(null)

      return 'OK'
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setVehicleArchiveError(err)
      } else {
        setVehicleArchiveError(new Error('An unknown error occurred'))
      }

      return null
    } finally {
      setLoadingPost(false)
    }
  }

  const vehicleRestore = async (_id: string) => {
    setLoadingPost(true)

    try {
      const apiUrl = makeUrlVehicleRestore()

      const toPost = { _id: _id }
      await customPost<void>(apiUrl, toPost)
      setVehicleRestoreError(null)

      return 'OK'
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setVehicleRestoreError(err)
      } else {
        setVehicleRestoreError(new Error('An unknown error occurred'))
      }

      return null
    } finally {
      setLoadingPost(false)
    }
  }
  const vehiclePublishLocal = async (_id: string, promoMsg: string) => {
    setLoadingPost(true)

    try {
      const apiUrl = makeUrlVehiclePublishLocal()

      const toPost = { _id, by: userData?.profile?._id, promoMsg }
      await customPost<void>(apiUrl, toPost)
      setVehiclePublishLocalError(null)

      return 'OK'
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setVehiclePublishLocalError(err)
      } else {
        setVehiclePublishLocalError(new Error('An unknown error occurred'))
      }

      return null
    } finally {
      setLoadingPost(false)
    }
  }

  // Exponemos estas variables al exterior
  return {
    errorPostVehicle,
    loadingPost,
    postVehicle,
    vehicleExpensesPost,
    vehicleExpensesPostError,
    vehicleArchive,
    vehicleArchiveError,
    vehicleRestore,
    vehicleRestoreError,
    vehiclePublishLocal,
    vehiclePublishLocalError,
  }
}
export default useVehiclePoster
